import ApiResponse from "@core/types/apiResponse";
import { ReportTagResponse } from "@features/report/tag/types/reportTag";
import { CreateReportTagRequest, UpdateReportTagRequest, RequestReportTagReopenRequest } from "@features/report/tag/types/reportTagRequests";
import { fetchChurchReportTag, fetchDistrictReportTag, fetchNationReportTag, fetchReportTagStatus, createReportTag, updateReportTag, fetchReportTagDepartmentReportStatusSummary, requestReportTagReopen, approveReportTagReopen, denyReportTagReopen } from "@features/report/tag/api/reportTagApi";
import { ReportTagStatusResponse } from "@features/report/tag/types/reportTagStatus";
import { DepartmentReportStatusSummary } from "@features/report/tag/types/departmentReportStatusSummary";

/**
 * Retrieves report tag data for the specified nation ID.
 *
 * This is a thin async wrapper around `fetchReportTag` that forwards the provided
 * parameters and returns the raw service response without modification.
 *
 * @param nationId - The numeric nation identifier for which to fetch tag data.
 * @param params - Optional query parameters forwarded to the underlying service
 * (e.g., filtering or pagination options). The expected shape depends on `fetchReportTag`.
 * @returns A promise that resolves to an `ApiResponse<ReportTagResponse>` containing the fetched tag information.
 *
 * @throws Error If the network request fails or the service returns an error.
 *
 * @remarks No validation or transformation is performed. Ensure that `params` matches the API's expected schema.
 *
 */
export const fetchChurchTagAsync = async (churchId: number, params: any): Promise<ApiResponse<ReportTagResponse>> => {
    const response = await fetchChurchReportTag(churchId, params);
    return response;
};

export const fetchDistrictTagAsync = async (districtId: number, params: any): Promise<ApiResponse<ReportTagResponse>> => {
    const response = await fetchDistrictReportTag(districtId, params);
    return response;
};

export const fetchNationTagAsync = async (nationId: number, params: any): Promise<ApiResponse<ReportTagResponse>> => {
    const response = await fetchNationReportTag(nationId, params);
    return response;
};

export const fetchTagStatusAsync = async (params: any): Promise<ApiResponse<ReportTagStatusResponse>> => {
    const response = await fetchReportTagStatus(params);
    return response;
}

/**
 * Asynchronously creates a new report tag associated with the given nation ID.
 *
 * Delegates to the underlying report-tag service and returns its API response.
 *
 * @param nationId - The numeric nation identifier the tag will be associated with.
 * @param data - The payload describing the tag to create.
 * @returns A promise that resolves to an ApiResponse containing the created ReportTagResponse.
 * @throws If the service call fails (e.g., network issues or validation errors).
 */
export const createTagAsync = async (nationId: number, data: CreateReportTagRequest): Promise<ApiResponse<any>> => {
    const response = await createReportTag(nationId, data);
    return response;
};

/**
 * Updates an existing report tag for the specified nation identifier.
 *
 * @remarks
 * This is a thin asynchronous wrapper around the report tag update service. It forwards
 * the provided payload and returns the API response containing the updated tag.
 *
 * @param nationId - The nation identifier associated with the report/tag context.
 * @param reportTagId - The unique identifier of the report tag to update.
 * @param data - The update payload for the report tag. The shape depends on the API contract.
 * @returns A promise that resolves to an API response wrapping the updated ReportTag entity.
 * @throws Rejects if the update operation fails due to network, validation, or server errors.
 *
 */
export const updateTagAsync = async (nationId: number, reportTagId: number, data: UpdateReportTagRequest): Promise<ApiResponse<any>> => {
    const response = await updateReportTag(nationId, reportTagId, data);
    return response;
};

export const fetchTagDepartmentReportStatusSummaryAsync = async (
    nationId: number,
    reportTagId: number
): Promise<ApiResponse<DepartmentReportStatusSummary>> => {
    return fetchReportTagDepartmentReportStatusSummary(nationId, reportTagId);
};

export const requestTagReopenAsync = async (nationId: number, reportTagId: number, data: RequestReportTagReopenRequest): Promise<ApiResponse<any>> => {
    const response = await requestReportTagReopen(nationId, reportTagId, data);
    return response;
};

export const approveTagReopenAsync = async (nationId: number, reportTagId: number): Promise<ApiResponse<any>> => {
    const response = await approveReportTagReopen(nationId, reportTagId);
    return response;
};

export const denyTagReopenAsync = async (nationId: number, reportTagId: number): Promise<ApiResponse<any>> => {
    const response = await denyReportTagReopen(nationId, reportTagId);
    return response;
};